import { useMutation, useQuery } from '@apollo/client';
import { AlertService, Button, InformationIconSmall, Modal, ModalContent, ModalFooter, OverlayService, Theme } from '@spoiler-alert/ui-library';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { SetNegotiationRules } from '../../graphql/mutations';
import { PossibleNegotiationsSummary, UserQuery } from '../../graphql/queries';
import StaticLoading from '../../pages/static-loading';

const useStyles = createUseStyles({
  container: {
    width: '750px',
  },
  row: {
    display: 'flex',
  },
  title: {
    fontSize: 24,
    fontWeight: 400,
    lineHeight: '28px',
    marginBottom: 24,
  },
  subTitle: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.176px',
    marginBottom: '8px',
  },
  infoText: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '-0.176px',
  },
  infoBox: {
    backgroundColor: Theme.grey10,
    padding: '12px',
    width: '50%',
    '&:last-child': {
      marginLeft: '12px',
    },
  },
  actionBox: {
    extend: 'infoBox',
    width: '55%',
    '&:last-child': {
      width: '45%',
      marginLeft: '12px',
    },
  },
  infoTitle: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '-0.072px',
    textTransform: 'uppercase',
  },
  infoBody: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '-0.084px',
  },
  lineBreak: {
    margin: '23px 0px 24px 0px',
    borderTop: `1px solid ${Theme.borderColor}`,
  },
  inputLine: {
    border: 'none',
    borderBottom: `solid 2px ${Theme.grey50}`,
    backgroundColor: Theme.grey10,
    width: 40,
    '&:focus': {
      outline: 'none',
      padding: '0 0 4px 0',
    },
  },
  inputLineFocused: {
    extend: 'inputLine',
    padding: '0 0 4px 0',
    '&:focus': {
      padding: 'none',
    },
  },
  inputLineError: {
    extend: 'inputLineFocused',
    borderBottom: `solid 2px ${Theme.red}`,
  },
  errorText: {
    marginTop: '4px',
    display: 'flex',
    textAlign: 'flex-start',
    color: Theme.red,
    fontSize: 12,
    fontWeight: 400,
    '& svg': {
      height: 16,
      width: 16,
      fill: Theme.red,
      marginRight: 4,
    },
  },
  link: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '-0.176px',
    color: Theme.teal,
    cursor: 'pointer',
  },
});

const NegotiationSettingsModalContent = ({ history, modalOpen, onSave, onCancel }) => {
  const classes = useStyles();
  const [lowerCutoff, setLowerCutoff] = useState(0);
  const [upperCutoff, setUpperCutoff] = useState(99);
  const [counterOfferNumber, setCounterOfferNumber] = useState(105);
  const { data, loading } = useQuery(PossibleNegotiationsSummary, { fetchPolicy: 'network-only' });
  const [setNegotiationRules, { loading: loadingSetRules }] = useMutation(SetNegotiationRules);

  const isReservePriceEnabled = data?.possibleNegotiationsSummaryQuery?.isReservePriceEnabled;
  const counterOfferError = (counterOfferNumber && upperCutoff !== null && counterOfferNumber <= upperCutoff) || counterOfferNumber == null;
  const lowerCutoffError = lowerCutoff == null;
  const upperCutoffError = (upperCutoff !== null && lowerCutoff !== null && upperCutoff < lowerCutoff) || upperCutoff == null;
  const invalidRules = lowerCutoff == null || upperCutoff == null || counterOfferNumber == null || counterOfferError || upperCutoffError;

  useEffect(() => {
    const { negotiationLowerCutoff, negotiationUpperCutoff, counterOfferNumber } = data?.possibleNegotiationsSummaryQuery || {};

    if (negotiationLowerCutoff || negotiationLowerCutoff === 0) {
      setLowerCutoff(negotiationLowerCutoff);
    }

    if (negotiationUpperCutoff || negotiationUpperCutoff === 0) {
      setUpperCutoff(negotiationUpperCutoff);
    }

    if (counterOfferNumber || counterOfferNumber === 0) {
      setCounterOfferNumber(counterOfferNumber);
    }
  }, [data]);

  const saveRules = async () => {
    const result = await setNegotiationRules({
      variables: {
        negotiationLowerCutoff: Number.parseInt(lowerCutoff, 10),
        negotiationUpperCutoff: Number.parseInt(upperCutoff, 10),
        counterOfferNumber: Number.parseInt(counterOfferNumber, 10),
      },
      refetchQueries: [{ query: UserQuery }],
    });
    if (result.errors || result.data.setNegotiationRules.errors.length > 0) {
      return AlertService.alert({
        type: 'warning',
        message: (
          <span>
            We were unable to save the rules due to an unknown error. Our team has been notified and are looking into the issue. Please contact
            customer support if the issue persists.
          </span>
        ),
        autoDismiss: true,
      });
    }
    onSave();
    return AlertService.alert({
      type: 'success',
      message: <span>Successfully saved negotiation rules</span>,
      autoDismiss: true,
    });
  };

  const handleLowerCutoffChange = (ev) => {
    if (ev.target.value === '') return setLowerCutoff(null);
    const parsed = Number.parseInt(ev.target.value, 10);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) return;
    setLowerCutoff(parsed);
  };

  const handleUpperCutoffChange = (ev) => {
    if (ev.target.value === '') return setUpperCutoff(null);
    const parsed = Number.parseInt(ev.target.value, 10);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) return;
    setUpperCutoff(parsed);
  };

  const handleCounterOfferChange = (ev) => {
    if (ev.target.value === '') return setCounterOfferNumber(null);
    const parsed = Number.parseInt(ev.target.value, 10);
    if (Number.isNaN(parsed) || parsed === 0) return;
    setCounterOfferNumber(parsed);
  };

  const counterOfferClass = () => {
    if (counterOfferError && counterOfferNumber) return classes.inputLineError;
    if (counterOfferNumber) return classes.inputLineFocused;
    return classes.inputLine;
  };

  const upperCutoffClass = () => {
    if (upperCutoffError && upperCutoff) return classes.inputLineError;
    if (upperCutoff) return classes.inputLineFocused;
    return classes.inputLine;
  };

  const navigateToItems = () => {
    OverlayService.hide();
    history.push('/items');
  };

  const infoBox = (title, body) => {
    return (
      <div className={classes.infoBox}>
        <h1 className={classes.infoTitle}>{title}</h1>
        <p className={classes.infoBody}>{body}</p>
      </div>
    );
  };

  const inputOptions = () => {
    return (
      <div>
        <h1 className={classes.subTitle}>Reserve Price</h1>
        <p className={classes.infoText}>
          Set your upper and lower threshold, as well as your counter offer value for negotiating offers below reserve price. These values will be
          used to limit negotiation suggestions.
        </p>
        <div className={classes.row}>
          <div className={classes.actionBox}>
            <h1 className={classes.infoTitle}>Stage offers that are</h1>
            <div>
              Between&nbsp;&nbsp;
              <input
                tabIndex={0}
                type="text"
                className={lowerCutoff ? classes.inputLineFocused : classes.inputLine}
                value={lowerCutoff}
                onChange={handleLowerCutoffChange}
              />
              %&nbsp;&nbsp; and{' '}
              <input tabIndex={0} type="text" className={upperCutoffClass()} value={upperCutoff} onChange={handleUpperCutoffChange} /> %&nbsp;&nbsp;
              of reserve price.
            </div>
            {lowerCutoffError && (
              <div className={classes.errorText}>
                <InformationIconSmall /> Lower cutoff percent must be set
              </div>
            )}
            {upperCutoffError && (
              <div className={classes.errorText}>
                <InformationIconSmall />
                {lowerCutoff !== null ? `Upper cutoff percent must be higher than ${lowerCutoff}%` : 'Upper cutoff percent must be set'}
              </div>
            )}
          </div>
          <div className={classes.actionBox}>
            <h1 className={classes.infoTitle}>set counter offers to</h1>
            <div>
              <input type="text" className={counterOfferClass()} value={counterOfferNumber} onChange={handleCounterOfferChange} />
              %&nbsp;&nbsp; of reserve price.
              {counterOfferError && (
                <div className={classes.errorText}>
                  <InformationIconSmall />
                  {upperCutoff !== null ? `Counter offer percent must be higher than ${upperCutoff}%` : 'Counter offer percent must be set'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const noReservePrice = () => {
    return (
      <div>
        <h1 className={classes.subTitle}>Reserve Price</h1>
        <div className={classes.infoText}>
          No reserve price is set. Set a reserve price on the{' '}
          <span className={classes.link} onClick={navigateToItems}>
            items screen
          </span>
          , or contact your customer success manager for more information.
        </div>
      </div>
    );
  };

  const displayDetails = () => {
    if (loading) {
      return (
        <div>
          <StaticLoading />
        </div>
      );
    }
    return isReservePriceEnabled ? inputOptions() : noReservePrice();
  };

  return (
    <Modal open={modalOpen} onHide={() => onCancel()} closeOnEsc closeOnOutsideClick>
      <div className={classes.container}>
        <ModalContent>
          <div data-testid="modal-title" className={classes.title}>
            Negotiation Settings
          </div>
          <div data-testid="modal-body" className={classes.modal__body}>
            <h1 className={classes.subTitle}>Highest historical transacted price</h1>
            <div className={classes.row}>
              {infoBox(
                'stage offers that are',
                'Below the highest historical transaction price for the customer for this item in the past 540 days.',
              )}
              {infoBox('set counter offers to', 'The customer’s highest historical transaction price for the item.')}
            </div>
            <div className={classes.lineBreak} />
            {displayDetails()}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button
            testId="cancel"
            className={classes.modal__cancel}
            type="button"
            onClick={() => onCancel()}
            secondary
            disabled={loading || loadingSetRules}
          >
            Cancel
          </Button>
          <Button
            testId="submit"
            type="submit"
            primary
            loadingText="Saving Rules"
            loading={loading || loadingSetRules}
            disabled={invalidRules || loading || loadingSetRules}
            onClick={saveRules}
          >
            Save Rules
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

NegotiationSettingsModalContent.propTypes = {
  history: PropTypes.object,
  modalOpen: PropTypes.bool,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
};

export default NegotiationSettingsModalContent;
